import { useEffect, useRef, useState } from 'react';
import { Check, Minus } from 'lucide-react';
import { cx } from '@/components/ui';

/**
 * File-manager style selection for a grid of cards or the rows of a table
 * (asset library, project browser). Items are identified by string keys in
 * display order.
 * - Click selects one, Shift-click a range, Ctrl/⌘-click toggles; taps on
 *   touch screens and Enter open instead (unless something is already selected).
 * - Dragging on empty space draws a box that selects what it touches; cards
 *   and rows carry `data-sel-key` so the box can find them.
 * - Ctrl/⌘+A selects all, Esc clears, Delete calls `onDelete`, F2 `onRename`.
 */
export function useSelection(order: string[], { resetOn, onDelete, onRename }: { resetOn: unknown[]; onDelete?: (keys: string[]) => void; onRename?: (key: string) => void }) {
  const [sel, setSel] = useState<Set<string>>(() => new Set());
  const anchor = useRef<string | null>(null);
  const [box, setBox] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const boxBase = useRef<Set<string>>(new Set());
  const gridRef = useRef<HTMLDivElement>(null);

  // Leaving the view drops the selection; items that disappear drop out of it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setSel(new Set()), resetOn);
  const orderKey = order.join(',');
  useEffect(() => {
    const shown = new Set(orderKey.split(','));
    setSel((s) => ([...s].every((k) => shown.has(k)) ? s : new Set([...s].filter((k) => shown.has(k)))));
  }, [orderKey]);

  const selected = order.filter((k) => sel.has(k));
  /** What an action on `k` applies to: the whole selection when `k` is part of it. */
  const targetsOf = (k: string) => (sel.has(k) && sel.size > 1 ? selected : [k]);
  const clear = () => setSel(new Set());
  /** Checkbox toggling: adds or removes one item without touching the rest. */
  const toggle = (k: string) => {
    setSel((s) => {
      const next = new Set(s);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
    anchor.current = k;
  };
  /** Select-all checkbox: everything shown, or nothing. */
  const setAll = (on: boolean) => setSel(on ? new Set(order) : new Set());

  const onItemClick = (e: React.MouseEvent, k: string, open: () => void) => {
    // Clicks from menus rendered in portals bubble through the card too.
    if (!(e.currentTarget as Node).contains(e.target as Node)) return;
    // Keyboard "clicks" (Enter on a link) and taps on touch screens open (unless picking several).
    if (e.detail === 0 || ((e.nativeEvent as PointerEvent).pointerType === 'touch' && !sel.size)) return open();
    e.preventDefault();
    if (e.metaKey || e.ctrlKey) {
      const next = new Set(sel);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      setSel(next);
      anchor.current = k;
    } else if (e.shiftKey && anchor.current && order.includes(anchor.current)) {
      const [i, j] = [order.indexOf(anchor.current), order.indexOf(k)].sort((x, y) => x - y);
      setSel(new Set(order.slice(i, j + 1)));
    } else {
      setSel(new Set([k]));
      anchor.current = k;
    }
  };
  /** Right-clicking something outside the selection selects just it. */
  const onItemContextMenu = (k: string) => {
    if (!sel.has(k)) setSel(new Set([k]));
  };

  // Box selection, started on the page's own empty space.
  const boxHandlers = {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0 || e.pointerType !== 'mouse') return;
      const target = e.target as HTMLElement;
      // Not from cards, controls, the details panel, or dialogs rendered through portals.
      if (!e.currentTarget.contains(target) || target.closest('[data-sel-key],button,a,input,select,textarea,[role=dialog],[data-selection-bar],[data-inspector]')) return;
      boxBase.current = e.shiftKey || e.metaKey || e.ctrlKey ? new Set(sel) : new Set();
      setBox({ x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY });
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!box) return;
      const b = { ...box, x1: e.clientX, y1: e.clientY };
      setBox(b);
      if (Math.abs(b.x1 - b.x0) < 4 && Math.abs(b.y1 - b.y0) < 4) return;
      const [l, r, t, btm] = [Math.min(b.x0, b.x1), Math.max(b.x0, b.x1), Math.min(b.y0, b.y1), Math.max(b.y0, b.y1)];
      const next = new Set(boxBase.current);
      for (const el of gridRef.current?.querySelectorAll<HTMLElement>('[data-sel-key]') ?? []) {
        const rc = el.getBoundingClientRect();
        if (rc.right >= l && rc.left <= r && rc.bottom >= t && rc.top <= btm) next.add(el.dataset.selKey!);
      }
      setSel(next);
    },
    onPointerUp: () => {
      if (!box) return;
      // A plain click on empty space clears the selection.
      if (Math.abs(box.x1 - box.x0) < 4 && Math.abs(box.y1 - box.y0) < 4) setSel(boxBase.current);
      setBox(null);
    },
  };
  const boxElement =
    box && (Math.abs(box.x1 - box.x0) > 3 || Math.abs(box.y1 - box.y0) > 3) ? (
      <div
        data-select-box
        className="pointer-events-none fixed z-30 rounded border border-accent bg-accent/15"
        style={{ left: Math.min(box.x0, box.x1), top: Math.min(box.y0, box.y1), width: Math.abs(box.x1 - box.x0), height: Math.abs(box.y1 - box.y0) }}
      />
    ) : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('input,textarea,select,[contenteditable=true]') || document.querySelector('[role=dialog]')) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a' && order.length) {
        e.preventDefault();
        setSel(new Set(order));
      } else if (e.key === 'Escape' && sel.size) clear();
      else if ((e.key === 'Delete' || e.key === 'Backspace') && selected.length && onDelete) {
        e.preventDefault();
        onDelete(selected);
      } else if (e.key === 'F2' && selected.length === 1 && onRename) {
        e.preventDefault();
        onRename(selected[0]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return { sel, setSel, selected, targetsOf, clear, toggle, setAll, onItemClick, onItemContextMenu, boxHandlers, boxElement, boxing: !!box, gridRef };
}

/**
 * Dragging selected cards onto drop targets (folders, breadcrumbs). `problem`
 * says why the dragged keys can't go to a destination ('' when they can).
 */
export function useDragMove<D>(mime: string, { problem, onDrop }: { problem: (keys: string[], dest: D) => string; onDrop: (keys: string[], dest: D) => void }) {
  const [dropOn, setDropOn] = useState<string | null>(null);
  const dragKeys = useRef<string[]>([]);

  const dragStart = (e: React.DragEvent, keys: string[]) => {
    dragKeys.current = keys;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(mime, JSON.stringify(keys));
    if (keys.length > 1) {
      const ghost = document.createElement('div');
      ghost.textContent = `${keys.length} items`;
      ghost.style.cssText = 'position:fixed;top:-100px;left:0;padding:4px 10px;border-radius:8px;background:#7c5cff;color:#fff;font:600 13px system-ui';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 12, 12);
      setTimeout(() => ghost.remove(), 0);
    }
  };
  const dragEnd = () => setDropOn(null);
  /** Props for a drop target; `id` identifies it for highlighting. */
  const dropTarget = (id: string, dest: () => D) => ({
    'data-drop-target': dropOn === id || undefined,
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(mime) || problem(dragKeys.current, dest())) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      if (dropOn !== id) setDropOn(id);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropOn((d) => (d === id ? null : d));
    },
    onDrop: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(mime)) return;
      e.preventDefault();
      e.stopPropagation();
      setDropOn(null);
      onDrop(dragKeys.current, dest());
    },
  });
  return { dropOn, dragStart, dragEnd, dropTarget };
}

export const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/**
 * A selection checkbox for a card or table row. It is a button so the item's own
 * click handling (which selects just that item) leaves it alone. Hidden until the
 * item is hovered or focused, unless `show` (something is selected) or on touch screens.
 */
export function SelectCheck({ checked, mixed, show, onChange, label, className }: { checked: boolean; mixed?: boolean; show?: boolean; onChange: (on: boolean) => void; label: string; className?: string }) {
  const on = checked || !!mixed;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={mixed ? 'mixed' : checked}
      aria-label={label}
      data-select-check
      draggable={false}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      onDoubleClick={(e) => e.stopPropagation()}
      className={cx(
        'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border transition-opacity focus-visible:opacity-100',
        on ? 'border-accent bg-accent text-white' : 'border-line-2 bg-panel/90 text-transparent hover:border-accent',
        show || on ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        className,
      )}
    >
      {mixed ? <Minus size={12} strokeWidth={3} /> : <Check size={12} strokeWidth={3} />}
    </button>
  );
}
